import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { FunctionComponent, memo, useMemo, useState, Fragment } from 'react';

import useLinkProps from '../../../hooks/use-link-props';
import { escapeRegExp } from '../../../utils/escapeRegExp';
import TextInput from '../../ui/inputs/text';
import FeatureLink from '../../ui/links/featured';
import { useAppContext } from '../../providers/app-context';

const FaqsList: FunctionComponent<{
  faqsByGroup: Array<TFaqsByGroup>;
}> = ({ faqsByGroup }) => {
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const { isBparts } = useAppContext();

  const [search, setSearch] = useState<string>('');
  const filteredFaqs = useMemo(() => {
    if (search) {
      return faqsByGroup
        .map((group) => ({
          ...group,
          faqs: group.faqs.filter((fqa) =>
            fqa.question.match(new RegExp(escapeRegExp(search), 'i'))
          ),
        }))
        .filter((group) => group.faqs.length > 0);
    }

    return faqsByGroup;
  }, [search, faqsByGroup]);

  const regex = new RegExp(escapeRegExp(`\(${search}\)`), 'ig');
  return (
    <>
      <div>
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          label={t('faqs:filter_placeholder')}
          labelBehaviour="animated"
          variant="filter"
          border
        />
      </div>
      <div className="lg:col-span-3 px-4 mt-8">
        <div className="grid lg:grid-cols-3 lg:gap-x-6 lg:gap-y-12 ">
          {filteredFaqs &&
            filteredFaqs.map((item) => (
              <section
                key={item.group.slug}
                className="mb-4"
                data-cy="faqs-list"
              >
                <h2 className={cx("font-bold text-xl mb-4", isBparts ? 'text-blue-100' : 'text-brand-100')}>
                  {item.group.label}
                </h2>
                {item.faqs && (
                  <ul>
                    {item.faqs.map((faq) => (
                      <li key={faq.slug} className="mb-4">
                        <Link
                          passHref
                          {...getLink(
                            'faqs_group',
                            { group: item.group.slug },
                            faq.slug
                          )}
                          legacyBehavior
                        >
                          <FeatureLink variant="neutral">
                            {faq.question.split(regex).map((value, index) => {
                              if (value && value.match(regex) && search) {
                                return (
                                  <span
                                    key={value + index}
                                    className="font-bold text-blue-100"
                                  >
                                    {value}
                                  </span>
                                );
                              }
                              return (
                                <Fragment key={value + index}>{value}</Fragment>
                              );
                            })}
                          </FeatureLink>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
        </div>
      </div>
    </>
  );
};

export default memo(FaqsList);
